import {
  SalesforceResourceMapping,
  SalesforceSyncTypes,
} from 'module/api/integrations/salesforce/types';
import {
  BySalesforceResourceMap,
  PartialSalesforceFieldMappingLocalData,
  SalesforceFieldMappingLocalData,
  SalesforceMappingOption,
  SalesforceObjectOption,
  SalesforceResourceMappingLocalData,
  SalesforceSyncOption,
} from 'module/api/integrations/salesforce/types';
import { useTranslation } from 'module/hooks/use-translation';
import { showToast } from 'module/ui/components/molecules/toasts/toasts';
import { ToastType } from 'module/ui/components/molecules/toasts/types';
import { keyBy, omit } from 'lodash';
import {
  useSalesForceDeleteMappingMutation,
  useSalesForceDeleteResourceMutation,
  useSalesForceExternalResourceTypes,
  useSalesforceMappingCreateMutation,
  useSalesforceMappingUpdateMutation,
  useSalesForceResourceMappings,
} from 'queries/salesforce';
import { useEffect, useMemo, useState } from 'react';
import { numberSorter } from 'utils/number-sorter';
import { getNewMap, serialiseMappingData } from './utils';

export const syncOptions: SalesforceSyncOption[] = [
  {
    label: SalesforceSyncTypes.SyncExternalToNative,
    value: SalesforceSyncTypes.SyncExternalToNative,
  },
  {
    label: SalesforceSyncTypes.SyncTwoWay,
    value: SalesforceSyncTypes.SyncTwoWay,
  },
  {
    label: SalesforceSyncTypes.SyncNativeToExternal,
    value: SalesforceSyncTypes.SyncNativeToExternal,
  },
];

type ResourceTypeOptions = {
  projects: SalesforceObjectOption[];
  accounts: SalesforceObjectOption[];
  users: SalesforceObjectOption[];
};

export const useFieldMappingActions = (
  integrationId: number,
  nativeResourceType?: 'project' | 'company' | 'user',
) => {
  const errorMessage = useTranslation('error');
  const resourceDeleteSuccessMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.deleted.message',
  );
  const mappingDeleteSuccessMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.field.toast.deleted.message',
  );
  const mappingCreateErrorMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.mapping.create.error.message',
  );
  const mappingUpdateErrorMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.mapping.update.error.message',
  );

  const [resources, setResources] = useState<BySalesforceResourceMap>({});
  const { data, status: resourceMappingsStatus } =
    useSalesForceResourceMappings(integrationId, nativeResourceType);
  const { data: resourceTypeList, status: resourceTypeStatus } =
    useSalesForceExternalResourceTypes(integrationId);
  const { mutate: deleteResourceMutation } =
    useSalesForceDeleteResourceMutation(Number(integrationId));
  const { mutate: deteleMappingMutation } =
    useSalesForceDeleteMappingMutation(integrationId);

  const { mutate: createMappingMutation } =
    useSalesforceMappingCreateMutation(integrationId);
  const { mutate: updateMappingMutation } =
    useSalesforceMappingUpdateMutation(integrationId);

  useEffect(() => {
    const transformedResources = data?.map(
      (each) =>
        ({
          ...each,
          id: each.resourceMappingConfigId.toString(),
          fieldMappings: each.fieldMappings
            ?.map(
              (fieldMapping) =>
                ({
                  ...fieldMapping,
                  id: fieldMapping?.fieldMappingConfigId?.toString(),
                  salesforceField: {
                    value: fieldMapping?.externalFieldId?.toString(),
                    valueStr: fieldMapping?.externalFieldId?.toString(),
                    label: fieldMapping.externalFieldName,
                  },
                  rocketlaneField: {
                    value: fieldMapping?.nativeFieldId?.toString(),
                    valueStr: fieldMapping?.nativeFieldId?.toString(),
                    label:
                      fieldMapping.nativeFieldLabel ??
                      fieldMapping.nativeFieldName,
                    name: fieldMapping.nativeFieldName,
                  },
                  syncField: fieldMapping?.syncType?.toString(),
                }) as SalesforceFieldMappingLocalData,
            )
            .sort((a, b) =>
              numberSorter(a?.fieldMappingConfigId, b?.fieldMappingConfigId),
            ),
        }) as SalesforceResourceMappingLocalData,
    );

    setResources(keyBy(transformedResources, 'id'));
  }, [data]);

  /**
   * Adding Resource
   * @param resourceObject
   */
  const onResourceAdd = (resourceObject: SalesforceResourceMapping) => {
    if (resourceObject == null) {
      return false;
    }

    return setResources((savedResources) => ({
      ...savedResources,
      [resourceObject.resourceMappingConfigId]: resourceObject,
    }));
  };

  /**
   * Removing Resource
   * @param resourceId
   */
  const onResourceRemove = (resourceId: number) => {
    if (resourceId == null) {
      return false;
    }

    const removedResource = resources[resourceId];

    setResources((savedResources) =>
      omit(savedResources, [resourceId.toString()]),
    );

    return deleteResourceMutation(resourceId, {
      onSuccess: () => {
        showToast({
          toastContainer: { current: document.body },
          content: resourceDeleteSuccessMessage,
          variant: ToastType.Success,
        });
      },
      onError: () => {
        setResources((savedResources) => ({
          ...savedResources,
          [resourceId]: removedResource,
        }));
      },
    });
  };

  /**
   * Adding Field Mapping to resource
   * @param resourceId
   */
  const onMappingAdd = (
    resourceId: number,
    externalField: SalesforceMappingOption<boolean> | null,
  ) => {
    if (resourceId == null) {
      return false;
    }

    let updatedResource = { ...resources[resourceId] };
    const hasFieldMappings = Object.prototype.hasOwnProperty.call(
      updatedResource,
      'fieldMappings',
    );

    if (!hasFieldMappings) {
      updatedResource = {
        ...updatedResource,
        fieldMappings: [],
      };
    }

    updatedResource?.fieldMappings?.push(
      getNewMap(
        integrationId,
        Number(updatedResource.id),
        externalField,
      ) as SalesforceFieldMappingLocalData,
    );

    return setResources((_mappings) => ({
      ..._mappings,
      [resourceId]: { ...updatedResource },
    }));
  };

  /**
   * Removing mapping from a resource
   * @param resourceId
   * @param fieldMapping
   */
  const onMappingRemove = async (
    resourceId: number,
    fieldMapping: SalesforceFieldMappingLocalData,
  ) => {
    if (resourceId == null || fieldMapping == null) {
      return false;
    }

    const updatedResource = { ...resources[resourceId] };

    // Store the previous state before optimistic update
    const previousFieldMappings = updatedResource.fieldMappings;

    setResources((_mappings) => {
      return {
        ..._mappings,
        [resourceId]: {
          ...updatedResource,
          fieldMappings: updatedResource.fieldMappings.filter(
            (fieldMap) => fieldMap.id !== fieldMapping.id,
          ),
        },
      };
    });
    if (fieldMapping.fieldMappingConfigId != null) {
      deteleMappingMutation(
        { resourceId, mappingId: fieldMapping.fieldMappingConfigId },
        {
          onSuccess: () => {
            showToast({
              toastContainer: { current: document.body },
              content: mappingDeleteSuccessMessage,
              variant: ToastType.Success,
            });
          },
          onError: () => {
            showToast({
              toastContainer: { current: document.body },
              content: errorMessage,
              variant: ToastType.Error,
            });

            // Rollback using the previous state, not the updated one
            setResources((_resources) => ({
              ..._resources,
              [resourceId]: {
                ...updatedResource,
                fieldMappings: previousFieldMappings, // Use stored previous state
              },
            }));
          },
        },
      );
    }

    return resources;
  };

  /**
   * Changing or updating the fields mapping
   * @param resourceId
   * @param mappingId
   * @param payload
   */
  const onMappingChange = async (
    resourceId: number,
    mappingId: string,
    payload: PartialSalesforceFieldMappingLocalData,
  ) => {
    if (resourceId == null || mappingId == null || payload == null) {
      return false;
    }

    const updatedResource = {
      ...resources[resourceId],
    } as SalesforceResourceMappingLocalData;

    if (updatedResource != null) {
      const updatedMappingsIndex = (
        updatedResource.fieldMappings ?? []
      ).findIndex((fieldMapping) => fieldMapping.id === mappingId.toString());

      if (
        updatedMappingsIndex >= 0 &&
        updatedResource.fieldMappings[updatedMappingsIndex] != null
      ) {
        updatedResource.fieldMappings[updatedMappingsIndex] =
          payload as SalesforceFieldMappingLocalData;

        // update locally
        setResources((savedMappings) => ({
          ...savedMappings,
          [resourceId]: updatedResource,
        }));

        const serialisedMappingData = serialiseMappingData(
          updatedResource.fieldMappings[updatedMappingsIndex],
        );

        if (serialisedMappingData != null) {
          if (serialisedMappingData?.fieldMappingConfigId) {
            updateMappingMutation(
              {
                resourceId,
                fieldMappingId: serialisedMappingData.fieldMappingConfigId,
                mappingData: serialisedMappingData,
              },
              {
                onError: () => {
                  showToast({
                    toastContainer: { current: document.body },
                    content: mappingUpdateErrorMessage,
                    variant: ToastType.Error,
                  });
                },
              },
            );
          } else {
            createMappingMutation(
              {
                resourceId,
                mappingData: serialisedMappingData,
              },
              {
                onError: () => {
                  showToast({
                    toastContainer: { current: document.body },
                    content: mappingCreateErrorMessage,
                    variant: ToastType.Error,
                  });
                },
              },
            );
          }
        }
      }
    }

    return resources;
  };

  const resourceTypeOptions = useMemo<ResourceTypeOptions>(() => {
    if (!resourceTypeList) {
      return {
        projects: [],
        accounts: [],
        users: [],
      };
    }

    return {
      projects:
        resourceTypeList.project?.map((salesforceObject) => ({
          label: salesforceObject.name,
          value: salesforceObject.id,
        })) || [],
      accounts:
        resourceTypeList.company?.map((salesforceObject) => ({
          label: salesforceObject.name,
          value: salesforceObject.id,
        })) || [],
      users:
        resourceTypeList.user?.map((salesforceObject) => ({
          label: salesforceObject.name,
          value: salesforceObject.id,
        })) || [],
    };
  }, [resourceTypeList]);

  return {
    resources,
    resourceMappingsStatus,
    resourceTypeOptions,
    resourceTypeStatus,
    onMappingAdd,
    onResourceAdd,
    onResourceRemove,
    onMappingRemove,
    onMappingChange,
  };
};
